import React, { createContext, useContext, useEffect, useState } from 'react';

interface IDictionary<T> {
    [id: string]: T;
}

const defaultValue: IBreakpoints = {} as IBreakpoints;

const BreakpointContext: React.Context<IBreakpoints> = createContext(defaultValue);

const BreakpointProvider = ({ children, queries }) => {
    const [queryMatch, setQueryMatch]: [IBreakpoints, (value: IBreakpoints) => void] = useState(
        {} as IBreakpoints,
    );

    useEffect(() => {
        const mediaQueryLists: IDictionary<MediaQueryList> = {};
        const keys: string[] = Object.keys(queries);

        let isAttached = false;

        const eventAdmin = (add: boolean): void => {
            keys.forEach((media: string): void => {
                if (typeof queries[media] === 'string') {
                    mediaQueryLists[media][add ? 'addListener' : 'removeListener'](
                        handleQueryListener,
                    );
                }
            });
        };

        const handleQueryListener = (): void => {
            const updatedMatches: IDictionary<boolean> = keys.reduce(
                (acc: IDictionary<boolean>, media: string) => {
                    acc[media] = !!mediaQueryLists[media]?.matches;

                    return acc;
                },
                {},
            );

            setQueryMatch(updatedMatches as unknown as IBreakpoints);
        };

        if (window?.matchMedia) {
            const matches: IDictionary<boolean> = {};

            keys.forEach((media: string): void => {
                if (typeof queries[media] !== 'string') {
                    matches[media] = false;

                    return;
                }

                mediaQueryLists[media] = window.matchMedia(queries[media]);
                matches[media] = mediaQueryLists[media].matches;
            });

            setQueryMatch(matches as unknown as IBreakpoints);

            isAttached = true;

            eventAdmin(true);
        }

        return () => {
            if (isAttached) {
                eventAdmin(false);
            }
        };
    }, [queries]);

    return <BreakpointContext.Provider value={queryMatch}>{children}</BreakpointContext.Provider>;
};

const useBreakpoints = (): IBreakpoints => {
    const context: IBreakpoints = useContext(BreakpointContext);

    if (context === ({} as IBreakpoints)) {
        throw new Error('useBreakpoint must be used within BreakpointProvider');
    }

    return context;
};

export default useBreakpoints;

export interface IBreakpoints {
    isDesktop: boolean;
    isTablet: boolean;
    isMobile: boolean;
}

export { BreakpointContext, BreakpointProvider };
